/* istanbul ignore file - complains about function scopes and logic */
import { Button, Icon } from '@distribusion/owl-frontend-ui'
import { format, subMinutes, parse, isBefore } from 'date-fns'
import React from 'react'

import useIsMobile from '@hooks/useIsMobile'
import useSearchParams from '@hooks/useSearchParams'
import date from '@lib/date'
import { useTranslation } from '@lib/i18n'

import '@components/JourneyList/LoadConnectionsButton/styles.scss'

type Direction = 'earlier' | 'later'

interface LoadConnectionsButtonProps {
  direction: Direction
  lastItem?: Connection
  firstItem?: Connection
  earlierTimeAllowance?: number | undefined
}

const LoadConnectionsButton = ({
  direction,
  lastItem,
  earlierTimeAllowance,
  firstItem,
}: LoadConnectionsButtonProps) => {
  const startOfDay = date.startOfDay()
  const endOfDay = date.endOfDay()
  const [queryParams, setSearchParams] = useSearchParams()
  const { departureTime } = queryParams
  const isMobile = useIsMobile()

  const getEarlierDepartureTime = (): string | undefined => {
    if (!firstItem?.departureTime) {
      return
    }
    const timeString = firstItem.departureTime.split('T')[1]?.substring(0, 5)
    const parsedTime = parse(timeString, 'HH:mm', new Date())

    const earlierTime = subMinutes(parsedTime, earlierTimeAllowance || 90)

    const minTime = parse('00:00', 'HH:mm', new Date())

    if (isBefore(earlierTime, minTime)) {
      return '00:00'
    }

    return format(earlierTime, 'HH:mm')
  }

  const getLaterDepartureTime = (): string => {
    const timeToUse = lastItem?.departureTime ? lastItem.departureTime.split('T')[1]?.substring(0, 5) : endOfDay
    return timeToUse
  }

  const isEarlierDisabled = (): boolean => {
    if (!firstItem?.departureTime) {
      return true
    }

    if (departureTime === '00:00') {
      return true
    }

    const firstItemTimeString = firstItem.departureTime.split('T')[1]?.substring(0, 5)
    const firstItemTime = parse(firstItemTimeString, 'HH:mm', new Date())
    const earlierTime = subMinutes(firstItemTime, earlierTimeAllowance || 90)
    const minTime = parse('00:00', 'HH:mm', new Date())

    return isBefore(earlierTime, minTime)
  }

  const isLaterDisabled = (): boolean => {
    if (!lastItem?.departureTime) {
      return true
    }

    if (departureTime === endOfDay) {
      return true
    }

    const lastItemTimeString = lastItem.departureTime.split('T')[1]?.substring(0, 5)
    const lastItemTime = parse(lastItemTimeString, 'HH:mm', new Date())
    const laterTime = subMinutes(lastItemTime, -(earlierTimeAllowance || 90))
    const maxTime = parse(endOfDay, 'HH:mm', new Date())

    return laterTime.getTime() > maxTime.getTime()
  }

  const isDisabled = () => (direction === 'earlier' ? isEarlierDisabled() : isLaterDisabled())

  const loadConnections = (newDepartureTime: string) => {
    setSearchParams(queryParams => ({
      ...queryParams,
      departureTime: newDepartureTime,
      earlierLater: true,
    }))
  }

  const handleClick = (): void => {
    if (isDisabled()) {
      return
    }

    if (direction === 'earlier') {
      const newDepartureTime = getEarlierDepartureTime() || startOfDay

      const currentTime = parse(departureTime, 'HH:mm', new Date())
      const calculatedTime = parse(newDepartureTime, 'HH:mm', new Date())

      if (calculatedTime.getTime() >= currentTime.getTime()) {
        const furtherEarlierTime = subMinutes(currentTime, earlierTimeAllowance || 90)
        const minTime = parse('00:00', 'HH:mm', new Date())

        if (isBefore(furtherEarlierTime, minTime)) {
          loadConnections('00:00')
          return
        }

        const adjustedDepartureTime = format(furtherEarlierTime, 'HH:mm')
        loadConnections(adjustedDepartureTime)
        return
      }

      loadConnections(newDepartureTime)
    } else {
      const newLaterDepartureTime = getLaterDepartureTime()
      loadConnections(newLaterDepartureTime)
    }
  }

  const { t } = useTranslation()
  const label = direction === 'earlier' ? 'journeyList.filters.showEarlier' : 'journeyList.filters.showLater'

  if (isMobile) {
    return (
      <div className="load-connections-button-mobile mb-3 w-100">
        <Button variant="outline" onClick={handleClick} disabled={isDisabled()}>
          {t(label)}
        </Button>
      </div>
    )
  }

  return (
    <div role="button" aria-disabled={isDisabled()} onClick={handleClick} className="load-connections">
      {t(label)}
      <Icon name={`${direction === 'earlier' ? 'up' : 'down'}-arrow`} size="medium" />
    </div>
  )
}

export default LoadConnectionsButton
